import { eventEndGame, eventStartGame, trackEvent } from "../../../utils/gtag";
import { useState, useEffect, useRef, useCallback } from "react";
import { useGame } from "../../../context/GameContext";
import { restartGame } from "../../../utils/gameControls";
import "./GameOver.scss";
import { Color } from "chess.js";
import { setActiveRow, setActiveCol, KeyCodes } from "../../../utils/navigationHelpers";
import VictoryScreen from "../../visual/VictoryScreen/VictoryScreen";
import { VictoryScreenConstants } from "../../../utils/constants";
import ChessButton from "../../visual/ChessButton/ChessButton";
import useSoundEffect from "../../../hooks/useSoundEffect";
import { useMultiplayer } from "../../../context/MultiplayerContext";
import PlayAgainResponseModal from "./PlayAgainResponseModal";
import PlayAgainRequestStatus from "./PlayAgainRequestStatus";
import { removeSavedLobby } from "../../../utils/multiplayer/savedLobbyManager";

/**
 * GameOver component
 * Handles the game over modal, allows the user to restart the game or go back to the home screen
 * @returns the game over modal
 */
export default function GameOver() {
    const { gameState, setGameState, setRemoteNavigation } = useGame();
    const { multiplayerState, requestToPlayAgain, handleReturnMainMenu } = useMultiplayer();

    const { game } = gameState;
    if (!game) throw new Error("Game is null");
    const { isForfeit } = multiplayerState.forfeit;
    const gameIsOver = game.isGameOver() || isForfeit;

    if (gameIsOver) removeSavedLobby();
    const isMultiplayerGame = !gameState.isAIGame;
    const isCheckmate = game?.isCheckmate();

    const endGameEventFired = useRef(false);
    const [shouldShowGameOver, setShouldShowGameOver] = useState(false);
    const [showVictoryScreen, setShowVictoryScreen] = useState(false);
    const { playSelectAudio, playClickAudio } = useSoundEffect();

    const VICTORYSCREEN_DELAY = 1700; // Delay before showing the victory screen

    const handleReviewGame = () => {
        setGameState((prev) => ({ ...prev, isReviewMode: true }));
        setRemoteNavigation((prev) => ({
            ...prev,
            isInGameOverMenu: false,
            selectedGameOverIcon: null,
            isInMenuIcons: true,
            selectedMenuIcon: null,
        }));
        playSelectAudio();
    };
    // Sets Focus on "Play Again" button when isReviewmode changes.
    useEffect(() => {
        setActiveRow(0);
        setActiveCol(0);
    }, [gameState.isReviewMode]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (KeyCodes.Ups.includes(event.key) || KeyCodes.Downs.includes(event.key)) {
                playClickAudio();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [playClickAudio]);

    const getGameEndReason = useCallback(() => {
        if (!gameIsOver) return null;

        if (isMultiplayerGame && isForfeit) return "Forfeit!";
        if (game.isCheckmate()) return "Checkmate";
        if (game.isStalemate()) return "Stalemate";
        if (game.isInsufficientMaterial()) return "Insufficient Material";
        if (game.isThreefoldRepetition()) return "Threefold Repetition";
        if (game.isDrawByFiftyMoves()) return "Draw by 50-move Rule";
        return null;
    }, [gameIsOver, game, isForfeit]);

    const getWinnerColor = useCallback((): Color | null => {
        if (!gameIsOver) return null;

        if (isMultiplayerGame && isForfeit) return multiplayerState.forfeit.winner.color;
        const loser = game.turn();
        const winner = loser === "w" ? "b" : "w";
        return winner;
    }, [gameIsOver, isMultiplayerGame, isForfeit, game]);

    const handlePlayAgain = () => {
        restartGame(setGameState);
        setRemoteNavigation((prev) => ({
            ...prev,
            isInGameOverMenu: false,
            selectedGameOverIcon: null,
        }));
    };

    const gameResult = getGameEndReason();
    const winnerColor = getWinnerColor();

    const winnerColorText = winnerColor === "w" ? "White" : "Black";
    const winnerName = isForfeit ? multiplayerState.forfeit.winner.username : winnerColorText;
    const descriptionText = game.isDraw() ? "Draw" : `${winnerName} wins!`;

    const isWinner = isMultiplayerGame
        ? multiplayerState.playerColor === winnerColor
        : winnerColor === "w";

    useEffect(() => {
        if (!gameIsOver) return;

        if (isCheckmate) {
            // For checkmate games, start with the victory screen sequence
            setShouldShowGameOver(false);
            const showTimeout = setTimeout(() => {
                setShowVictoryScreen(true);
            }, VICTORYSCREEN_DELAY);

            const hideTimeout = setTimeout(() => {
                setShowVictoryScreen(false);
                setShouldShowGameOver(true);
            }, VictoryScreenConstants.VICTORY_SCREEN_DURATION);

            return () => {
                clearTimeout(showTimeout);
                clearTimeout(hideTimeout);
            };
        } else {
            // For non-checkmate games, show immediately
            setShouldShowGameOver(true);
        }
    }, [gameIsOver, isCheckmate]);

    useEffect(() => {
        if (isMultiplayerGame && isWinner && !endGameEventFired.current) {
            eventEndGame(2);
            endGameEventFired.current = true;
            return;
        }

        const eventDetails = {
            event_category: "ai_game_outcome",
            reason: gameResult,
            winner: winnerColor,
            difficulty: gameState.aiDifficulty,
        };

        const eventType = isWinner ? "win" : "loss";
        trackEvent(eventType, eventDetails);

        eventEndGame(1);
        endGameEventFired.current = true;
    }, [game, gameState.isAIGame, winnerColor, multiplayerState.playerColor]);

    const isPlayAgainRequest =
        multiplayerState.playAgainRequest.receivedRequest ||
        multiplayerState.playAgainRequest.sentRequest;

    if (!gameResult) return null;
    if (!winnerColor) return null;
    if (!gameIsOver) return null;
    if (!game) return null;

    return (
        <>
            <div>{showVictoryScreen && winnerColor && <VictoryScreen winner={winnerColor} />}</div>
            {shouldShowGameOver && !showVictoryScreen && !gameState.isReviewMode && (
                <div className="game-over" data-testid="game-over">
                    <>
                        <h1>{gameResult}</h1>
                        <h2>{descriptionText}</h2>
                        {!gameState.isAIGame && !multiplayerState.isHost && (
                            <div className="game-over-multiplayer">
                                {!isPlayAgainRequest && !gameState.isPuzzle && (
                                    <ChessButton
                                        data-navrow={0}
                                        data-navcol={0}
                                        autoFocus
                                        onClick={() => {
                                            requestToPlayAgain();
                                        }}
                                    >
                                        Play Again
                                    </ChessButton>
                                )}

                                <PlayAgainResponseModal />
                                <PlayAgainRequestStatus />

                                <ChessButton
                                    data-navrow={
                                        multiplayerState.playAgainRequest.receivedRequest
                                            ? 2 // Accept=0, Decline=1, MainMenu=2
                                            : !isPlayAgainRequest
                                              ? 1 // PlayAgain=0, MainMenu=1
                                              : 0 // MainMenu=0 (when play again request sent but no response yet)
                                    }
                                    data-navcol={0}
                                    onClick={() => {
                                        handleReturnMainMenu();
                                    }}
                                >
                                    Main Menu
                                </ChessButton>

                                <ChessButton
                                    data-navrow={
                                        multiplayerState.playAgainRequest.receivedRequest
                                            ? 3 // Accept=0, Decline=1, MainMenu=2, ReviewBoard=3
                                            : !isPlayAgainRequest
                                              ? 2 // PlayAgain=0, MainMenu=1, ReviewBoard=2
                                              : 1 // MainMenu=0, ReviewBoard=1 (when play again request sent but no response yet)
                                    }
                                    data-navcol={0}
                                    onClick={handleReviewGame}
                                >
                                    Review Board
                                </ChessButton>
                            </div>
                        )}
                        {/*Show for AI game. */}
                        {gameState.isAIGame && (
                            <div className="game-over-button">
                                <ChessButton
                                    data-navrow={0}
                                    data-navcol={0}
                                    autoFocus
                                    onClick={() => {
                                        handlePlayAgain();
                                        eventStartGame(1);
                                    }}
                                >
                                    Play Again
                                </ChessButton>
                                <ChessButton
                                    data-navrow={1}
                                    data-navcol={0}
                                    onClick={() => {
                                        handleReturnMainMenu();
                                    }}
                                >
                                    Main Menu
                                </ChessButton>

                                <ChessButton
                                    data-navrow={2}
                                    data-navcol={0}
                                    onClick={handleReviewGame}
                                >
                                    Review Board
                                </ChessButton>
                            </div>
                        )}
                    </>
                </div>
            )}
        </>
    );
}
